// PersonaPicker — the first-visit landing. Shown when the user hasn't
// explicitly picked a persona yet (see auth/Persona.tsx hasPicked). Full
// screen, three cards, one click to enter the app in the right role's
// landing page.
//
// Kept deliberately sparse: each card is label + role subtitle + one
// purpose line. Long descriptions and "you'll start on X" footers were
// too much for first-visit context — people don't know how to react to
// a wall of text before they've seen the app.
import { useLocation } from "wouter";
import type { Persona } from "@release-hub/shared";
import { PERSONA_LABEL, usePersona } from "@/auth/Persona";
import { cn } from "@/lib/cn";

/** Where each persona should land after picking. EPD starts upstream at
 *  Releases (production surface); the two consumer personas land on the
 *  weekly digest. */
const LANDING: Record<Persona, string> = {
  epd: "/timeline",
  enablement: "/timeline",
  sales: "/timeline",
};

const SUBTITLE: Record<Persona, string> = {
  epd: "Engineering · Product · Design",
  enablement: "PMM · CS · Sales Engineering",
  sales: "Go-to-market",
};

/** One-line-ish purpose per card. Enough to answer "what would I use
 *  this for?" without becoming a wall of text — someone is skimming
 *  before they've even entered the app. */
const PURPOSE: Record<Persona, string> = {
  epd: "You author releases and feature launches. The pipeline starts here.",
  enablement: "What's shipping now, with the detail to brief customers.",
  sales: "What we shipped. Quick reference for customer calls.",
};

const ORDER: Persona[] = ["epd", "enablement", "sales"];

export function PersonaPicker() {
  const { setPersona } = usePersona();
  const [, navigate] = useLocation();

  const pick = (p: Persona) => {
    setPersona(p);
    navigate(LANDING[p]);
  };

  return (
    <div className="min-h-screen bg-bg px-6 py-16">
      <div className="mx-auto flex max-w-5xl flex-col gap-10">
        <header className="text-center">
          <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-text-tertiary">
            Release Hub
          </p>
          <h1 className="font-serif text-4xl leading-tight text-text">
            Who are you?
          </h1>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {ORDER.map((p) => (
            <PersonaCard key={p} persona={p} onPick={pick} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PersonaCard({
  persona,
  onPick,
}: {
  persona: Persona;
  onPick: (p: Persona) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(persona)}
      className={cn(
        "group flex flex-col gap-3 rounded-2xl border border-border bg-card p-6 text-left transition-all",
        "hover:-translate-y-0.5 hover:border-accent hover:shadow-lg",
        "focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
      )}
    >
      <div>
        <div className="mb-0.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">
          {SUBTITLE[persona]}
        </div>
        <div className="font-serif text-2xl leading-tight text-text">
          {PERSONA_LABEL[persona]}
        </div>
      </div>
      <p className="text-sm text-text-secondary">{PURPOSE[persona]}</p>
    </button>
  );
}
